import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Activity,
  RefreshCw,
  Clock,
  User,
  Zap,
  Bot,
  Filter,
  ChevronLeft,
  ChevronRight,
  FileText,
  Users,
  Mail,
  Calendar,
  DollarSign,
  Settings,
  Search,
  X,
  Eye,
  Edit,
  Trash2,
  Plus,
  Send,
  CheckCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ActivityLog = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  
  // Filtres
  const [filterCategory, setFilterCategory] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterActorType, setFilterActorType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Détails
  const [selectedLog, setSelectedLog] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: ((page - 1) * limit).toString()
      });
      
      if (filterCategory) params.append('category', filterCategory);
      if (filterAction) params.append('action', filterAction);
      if (filterActorType) params.append('actor_type', filterActorType);
      if (searchTerm) params.append('search', searchTerm);
      
      const response = await axios.get(`${API}/api/activity-logs?${params}`, getAuthHeaders());
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Erreur chargement logs:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Erreur lors du chargement du journal');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, page, limit, filterCategory, filterAction, filterActorType, searchTerm]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('fr-CA', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getActorIcon = (actorType) => {
    switch (actorType) {
      case 'user': return User;
      case 'automation': return Zap;
      case 'system': return Bot;
      default: return Activity;
    }
  };

  const getCategoryIcon = (category) => {
    switch (category?.toLowerCase()) {
      case 'contact': return Users;
      case 'deal': return DollarSign;
      case 'submission': return FileText;
      case 'contract': return FileText;
      case 'campaign': return Mail;
      case 'appointment': return Calendar;
      case 'settings': return Settings;
      default: return Activity;
    }
  };

  const getActionIcon = (action) => {
    if (!action) return Activity;
    if (action.includes('create') || action.includes('add')) return Plus;
    if (action.includes('update') || action.includes('edit')) return Edit;
    if (action.includes('delete') || action.includes('remove')) return Trash2;
    if (action.includes('view') || action.includes('read')) return Eye;
    if (action.includes('send') || action.includes('email')) return Send;
    if (action.includes('success') || action.includes('complete')) return CheckCircle;
    if (action.includes('error') || action.includes('fail')) return XCircle;
    if (action.includes('warning')) return AlertTriangle;
    return Activity;
  };

  const getActionColor = (action) => {
    if (!action) return { bg: '#F3F4F6', text: '#6B7280' };
    if (action.includes('create') || action.includes('add')) return { bg: '#D1FAE5', text: '#059669' };
    if (action.includes('update') || action.includes('edit')) return { bg: '#DBEAFE', text: '#2563EB' };
    if (action.includes('delete') || action.includes('remove')) return { bg: '#FEE2E2', text: '#DC2626' };
    if (action.includes('send') || action.includes('email')) return { bg: '#E0E7FF', text: '#4F46E5' };
    if (action.includes('error') || action.includes('fail')) return { bg: '#FEE2E2', text: '#DC2626' };
    return { bg: '#F3F4F6', text: '#6B7280' };
  };

  const totalPages = Math.ceil(total / limit);

  const clearFilters = () => {
    setFilterCategory('');
    setFilterAction('');
    setFilterActorType('');
    setSearchTerm('');
    setPage(1);
  };

  const hasActiveFilters = filterCategory || filterAction || filterActorType || searchTerm;

  const categories = ['contact', 'deal', 'submission', 'contract', 'campaign', 'appointment', 'settings', 'auth'];
  const actorTypes = ['user', 'automation', 'system'];

  return (
    <div className="min-h-screen" style={{ background: '#F8F8F6' }}>
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2" style={{ color: '#335C2E' }}>
                  <Activity size={24} />
                  Journal d'activité
                </h1>
                <p className="text-sm text-gray-500">
                  {total} actions enregistrées
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-3 py-2 rounded-lg flex items-center gap-2 text-sm transition-colors ${
                  showFilters || hasActiveFilters ? 'bg-green-100 text-green-700' : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                <Filter size={16} />
                Filtres
                {hasActiveFilters && (
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                )}
              </button>
              <button
                onClick={loadLogs}
                disabled={loading}
                className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center gap-2 text-sm disabled:opacity-50"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                Actualiser
              </button>
            </div>
          </div>

          {/* Barre de filtres */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Recherche */}
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
                
                {/* Catégorie */}
                <select
                  value={filterCategory}
                  onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}
                  className="px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">Toutes les catégories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
                  ))}
                </select>
                
                {/* Type d'acteur */}
                <select
                  value={filterActorType}
                  onChange={(e) => { setFilterActorType(e.target.value); setPage(1); }}
                  className="px-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">Tous les acteurs</option>
                  {actorTypes.map(type => (
                    <option key={type} value={type}>
                      {type === 'user' ? 'Utilisateur' : type === 'automation' ? 'Automatisation' : 'Système'}
                    </option>
                  ))}
                </select>
                
                {/* Bouton effacer */}
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-2 justify-center"
                  >
                    <X size={16} />
                    Effacer les filtres
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Contenu principal */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-sm">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw size={24} className="animate-spin text-gray-400" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Activity size={48} className="mx-auto mb-4 opacity-50" />
              <p>Aucune activité enregistrée</p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="mt-4 text-green-600 hover:underline text-sm"
                >
                  Effacer les filtres
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Liste des logs */}
              <div className="divide-y">
                {logs.map((log, index) => {
                  const ActorIcon = getActorIcon(log.actor_type);
                  const CategoryIcon = getCategoryIcon(log.action_category);
                  const ActionIcon = getActionIcon(log.action);
                  const actionColor = getActionColor(log.action);
                  
                  return (
                    <div 
                      key={log.id || index} 
                      className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => setSelectedLog(selectedLog?.id === log.id ? null : log)}
                    >
                      <div className="flex items-start gap-4">
                        {/* Icône catégorie */}
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: actionColor.bg }}
                        >
                          <ActionIcon size={20} style={{ color: actionColor.text }} />
                        </div>
                        
                        {/* Contenu */}
                        <div className="flex-grow min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <h3 className="font-medium text-gray-900 truncate">
                                {log.action_label || log.action}
                              </h3>
                              {log.description && (
                                <p className="text-sm text-gray-500 mt-0.5 truncate">
                                  {log.description}
                                </p>
                              )}
                            </div>
                            <div className="text-xs text-gray-400 flex items-center gap-1 flex-shrink-0">
                              <Clock size={12} />
                              {formatDate(log.created_at)}
                            </div>
                          </div>
                          
                          {/* Tags */}
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            {log.action_category && (
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 flex items-center gap-1">
                                <CategoryIcon size={10} />
                                {log.action_category}
                              </span>
                            )}
                            <span className="flex items-center gap-1 text-xs text-gray-500">
                              <ActorIcon size={12} />
                              {log.actor_name || (log.actor_type === 'system' ? 'Système' : 'Inconnu')}
                            </span>
                            {log.entity_type && log.entity_name && (
                              <span className="text-xs text-gray-400">
                                • {log.entity_type}: {log.entity_name}
                              </span>
                            )}
                          </div>
                          
                          {/* Détails expanded */}
                          {selectedLog?.id === log.id && (
                            <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm">
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <span className="text-gray-500">ID:</span>
                                  <span className="ml-2 font-mono text-xs">{log.id}</span>
                                </div>
                                {log.entity_id && (
                                  <div>
                                    <span className="text-gray-500">Entity ID:</span>
                                    <span className="ml-2 font-mono text-xs">{log.entity_id}</span>
                                  </div>
                                )}
                                {log.ip_address && (
                                  <div>
                                    <span className="text-gray-500">IP:</span>
                                    <span className="ml-2">{log.ip_address}</span>
                                  </div>
                                )}
                                {log.metadata && Object.keys(log.metadata).length > 0 && (
                                  <div className="col-span-2">
                                    <span className="text-gray-500">Métadonnées:</span>
                                    <pre className="mt-1 p-2 bg-white rounded text-xs overflow-auto max-h-32">
                                      {JSON.stringify(log.metadata, null, 2)}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t">
                  <p className="text-sm text-gray-500">
                    Page {page} sur {totalPages} ({total} résultats)
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={20} />
                    </button>
                    
                    {/* Numéros de pages */}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`w-8 h-8 rounded text-sm ${
                            page === pageNum 
                              ? 'text-white' 
                              : 'hover:bg-gray-100'
                          }`}
                          style={page === pageNum ? { backgroundColor: '#335C2E' } : {}}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight size={20} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ActivityLog;
